/* ══════════════════════════════════════════════════════════════════════
   dbakers Themed Cupcake Builder JS
   File: assets/dbakers-cupcake-themed-builder.js
   Prefix: dctb-
   Companion: snippets/dbakers-cupcake-themed-builder.liquid
              assets/dbakers-cupcake-themed-builder.css

   ── 2026-05-27 — Phase 3 rewrite ──
   Switched from <select>-based flavor pickers to a CARD-based system
   matching the regular + mini cupcake builders. Added a new "Dietary
   needs?" accordion step BEFORE flavors with two iOS-style toggles:
     - Gluten-free (existing fee logic preserved per box size)
     - Nut-free (free — no upcharge)
   Both surface warm-cream disclaimer cards with required acknowledgment
   checkboxes. Three flavors flagged as nut-containing:
     - Vanilla Cupcake filled with Nutella Fudge
     - Pistachio Cupcake filled with Raspberry Marmalade
     - Carrot Cupcake
   Cards live-filter when toggles change (Red Velvet hides on GF, nut
   flavors hide on Nut-Free). Active slot tracking auto-advances after
   each pick. Cart property combines "Gluten-free + Nut Allergy
   (acknowledged)" on the box line.

   ── 2026-05-27 — Ribbon color "Swipe for more →" hint ──
   The ribbon/topper color swatches scroll horizontally on mobile. A
   small fading "Swipe for more colors →" hint is now injected above
   the row when it overflows (mirrors the heart cake builder). See
   initRibbonSwipeHint() + the .dctb-swipe-hint CSS.

   ── 2026-05-21 — Cart-display fix ──
   Addon line items use BOTH _addon_for (parent cupcake-box line key)
   AND _addon (human-readable label) so the cart-items Liquid template
   rolls up addon prices into the box's displayed total. Same pattern
   as the signature cake builder. Without _addon, the box would charge
   correctly but display its base price only.

   Architecture:
     - Step machinery (is-locked → is-active → is-done), summaries +
       Change buttons, dynamic numbering via renumberSteps().
     - Cloudinary upload (cloud "dlocffoio", preset "dbakers_cake_photos")
       with 8s timeout + graceful fallback. Same as cake designer.
     - GF fee as separate line item (box-6 or box-12 variant).
     - Custom Color +$10 + Pearl Sprinkles +$10 as separate line items
       via window.ddbOptionVariants (hydrated by cake designer on every
       page; we ensure-hydrate as a fallback).
     - Topper char distribution: even spread across the box.
   ══════════════════════════════════════════════════════════════════════ */

(function() {
  'use strict';

  /* ── Cloudinary config (same account as cake designer) ── */
  var CLOUDINARY_CLOUD  = 'dlocffoio';
  var CLOUDINARY_PRESET = 'dbakers_cake_photos';
  var CLOUDINARY_URL    = 'https://api.cloudinary.com/v1_1/' + CLOUDINARY_CLOUD + '/image/upload';
  var UPLOAD_TIMEOUT_MS = 8000;

  /* ── Config from Liquid ── */
  var cfg = window.dctbProduct || {};

  /* ── State ── */
  var state = {
    boxSize: 6,

    /* Flavor slots — 4 slots, only first 2 used when boxSize=6.
       Each entry is either null (empty) or a flavor name (string).
       activeFlavorSlot tracks which slot the customer is currently
       picking; defaults to 1, advances to next empty after each pick. */
    flavors: [null, null, null, null],
    activeFlavorSlot: 1,

    /* Dietary — gfOn keeps the same semantics as before. nutOn is new,
       free, and removes nut-flavored options. Both toggles require
       an acknowledgment checkbox before cart submit. */
    gfOn: false,
    nutOn: false,
    gfAck: false,
    nutAck: false,

    /* Frosting */
    frostingColor: null,
    frostingHex: null,
    frostingIsLight: false,
    frostingIsCustom: false,
    customColorFee: 0,

    /* Two-tone (Custom Color optional, Butterfly required) */
    twoToneOn: false,
    secondColor: null,
    secondHex: null,
    secondIsLight: false,

    /* Ribbon (Coquette) */
    ribbonName: null,
    ribbonHex: null,

    /* Per-cupcake topper (Coquette + Say It) */
    topperRaw: '',
    topperChunks: [],
    topperColorName: null,
    topperColorHex: null,

    /* Sprinkles */
    sprinkleKey: null,
    sprinkleName: null,
    sprinklePrice: 0,
    sprinkleIsPearl: false,

    /* Pearls (Butterfly) */
    pearlKey: null,
    pearlName: null,

    /* Filling (Gender Reveal) */
    fillingChoice: null,

    /* Photos */
    photos: {},
    activeCupcake: 0,
    pendingTargetCupcake: 0,
    sharedPhoto: null
  };

  var cropperInstance = null;

  /* ── Logging ── */
  function dlog() { if (window.dctbDebug) console.log.apply(console, arguments); }
  function dwarn() { if (window.dctbDebug) console.warn.apply(console, arguments); }

  /* ── DOM helpers ── */
  function $(id) { return document.getElementById(id); }
  function $$(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }
  function on(el, ev, fn) { if (el) el.addEventListener(ev, fn); }
  function setText(id, val) { var el = $(id); if (el) el.textContent = val; }
  function setHTML(id, html) { var el = $(id); if (el) el.innerHTML = html; }

  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function isLightHex(hex) {
    if (!hex) return false;
    var h = String(hex).replace('#', '');
    if (h.length === 3) h = h[0]+h[0]+h[1]+h[1]+h[2]+h[2];
    if (h.length !== 6) return false;
    var r = parseInt(h.substring(0,2), 16);
    var g = parseInt(h.substring(2,4), 16);
    var b = parseInt(h.substring(4,6), 16);
    if (isNaN(r) || isNaN(g) || isNaN(b)) return false;
    return (0.299*r + 0.587*g + 0.114*b) > 220;
  }


  /* ══════════════════════════════════════════════════════════════
     INIT
     ══════════════════════════════════════════════════════════════ */
  function init() {
    var root = $('dctb-root');
    if (!root) return;

    state.boxSize = parseInt(root.dataset.boxSize, 10) || 6;

    /* Hydrate ddbOptionVariants if not already loaded (cake designer
       does this; fallback in case cupcake page loads before it). */
    ensureOptionVariantsHydrated();

    function safe(name, fn) {
      try { fn(); }
      catch (err) { console.error('[dctb] ' + name + ' failed:', err); }
    }

    safe('renumberSteps',         renumberSteps);
    safe('applyTwoToneDefaults',  applyTwoToneDefaults);
    safe('bindSizeCards',         bindSizeCards);
    safe('bindGfSwitch',          bindGfSwitch);
    safe('bindNutSwitch',         bindNutSwitch);
    safe('bindGfAck',             bindGfAck);
    safe('bindNutAck',            bindNutAck);
    safe('bindDietSkip',          bindDietSkip);
    safe('bindFlavorCards',       bindFlavorCards);
    safe('bindFlavorSlots',       bindFlavorSlots);
    safe('bindFrostingSwatches',  bindFrostingSwatches);
    safe('bindSecondFrosting',    bindSecondFrosting);
    safe('bindCustomColorPicker', bindCustomColorPicker);
    safe('bindTwoToneToggle',     bindTwoToneToggle);
    safe('bindRibbonCells',       bindRibbonCells);
    safe('bindTopperInput',       bindTopperInput);
    safe('bindTopperColorBtn',    bindTopperColorBtn);
    safe('bindTopperColorGrid',   bindTopperColorGrid);
    safe('bindSprinkleCards',     bindSprinkleCards);
    safe('bindPearlCards',        bindPearlCards);
    safe('bindFillingCards',      bindFillingCards);
    safe('bindAddonRows',         bindAddonRows);
    safe('bindTypePills',         bindTypePills);
    safe('bindImgTiles',          bindImgTiles);
    safe('bindNumberCandleInput', bindNumberCandleInput);
    safe('bindGiftTextarea',      bindGiftTextarea);
    safe('bindDropZone',          bindDropZone);
    safe('bindBoxSlots',          bindBoxSlots);
    safe('bindFileInput',         bindFileInput);
    safe('bindChangeButtons',     bindChangeButtons);
    safe('bindStickyButton',      bindStickyButton);
    safe('bindCartErrorClose',    bindCartErrorClose);
    safe('bindCropperModalControls', bindCropperModalControls);

    safe('refreshSlotsForBoxSize', refreshSlotsForBoxSize);
    safe('refreshGfFee',          refreshGfFee);
    safe('refreshFlavorCards',    refreshFlavorCards);
    safe('refreshFlavorPrompt',   refreshFlavorPrompt);
    safe('autoActivateStep1',     autoActivateStep1);
    safe('initRibbonSwipeHint',   initRibbonSwipeHint);
    safe('updateProgress',        updateProgress);
    safe('updateStickyStatus',    updateStickyStatus);
  }

  if (document.readyState !== 'loading') {
    setTimeout(init, 50);
  } else {
    document.addEventListener('DOMContentLoaded', function() { setTimeout(init, 50); }, { once: true });
  }


  /* ══════════════════════════════════════════════════════════════
     OPTION VARIANTS — Custom Color +$10, Pearl Sprinkles +$10
     Cake designer hydrates window.ddbOptionVariants from a Liquid
     dump. This is a safety-net fetch in case it didn't load (e.g.
     direct cupcake page visit).
     ══════════════════════════════════════════════════════════════ */
  function ensureOptionVariantsHydrated() {
    if (window.ddbOptionVariants && Object.keys(window.ddbOptionVariants).length > 0) return;
    fetch('/products/heart-cake-custom-options.js', { headers: { 'Accept': 'application/json' } })
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(prod) {
        if (!prod || !prod.variants) return;
        window.ddbOptionVariants = window.ddbOptionVariants || {};
        prod.variants.forEach(function(v) {
          window.ddbOptionVariants[v.title] = v.id;
        });
        dlog('[dctb] Hydrated ddbOptionVariants:', Object.keys(window.ddbOptionVariants));
      })
      .catch(function(err) { dwarn('[dctb] hydrate fail:', err.message); });
  }

  function lookupOptionVariant(title) {
    var map = window.ddbOptionVariants || {};
    if (map[title]) return map[title];
    /* Loose match — strip whitespace + lowercase. */
    var norm = String(title).toLowerCase().replace(/\s+/g, ' ').trim();
    var keys = Object.keys(map);
    for (var i = 0; i < keys.length; i++) {
      if (keys[i].toLowerCase().replace(/\s+/g, ' ').trim() === norm) return map[keys[i]];
    }
    return null;
  }


  /* ══════════════════════════════════════════════════════════════
     STEP MACHINERY
     ══════════════════════════════════════════════════════════════ */

  /* Renumber visible steps 1..N. Per-product some are skipped — this
     resequences the small ".dctb-step-num" badges so customers see
     1, 2, 3, … in the order they actually appear. */
  function renumberSteps() {
    var steps = $$('.dctb-step');
    steps.forEach(function(stepEl, idx) {
      var num = stepEl.querySelector('.dctb-step-num');
      if (num) num.textContent = String(idx + 1);
    });
  }

  function unlockStep(stepEl) {
    if (!stepEl) return;
    stepEl.classList.remove('is-locked', 'is-done');
    stepEl.classList.add('is-active');
    /* The color rows (ribbon + frosting) are display:none until their
       step unlocks, so their scrollWidth is 0 at page load and the swipe
       hint can't measure them. Re-attempt now that a step has opened.
       initRibbonSwipeHint is idempotent (guards double-inject) and no-ops
       if a row still isn't visible/overflowing. We retry on a couple of
       delays because right after display flips, swatch images may not have
       laid out yet — so a single rAF can read scrollWidth before overflow
       exists. The staggered retries catch it once layout settles. */
    var tryHint = function() { try { initRibbonSwipeHint(); } catch (e) {} };
    if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(tryHint);
    } else {
      tryHint();
    }
    setTimeout(tryHint, 120);
    setTimeout(tryHint, 400);
  }

  function completeStep(stepEl, summaryHTML) {
    if (!stepEl) return;
    stepEl.classList.remove('is-locked', 'is-active');
    stepEl.classList.add('is-done');
    if (summaryHTML) {
      var sum = stepEl.querySelector('.dctb-step-summary');
      if (sum) {
        sum.innerHTML = summaryHTML;
        sum.hidden = false;
      }
    }
    var ch = stepEl.querySelector('.dctb-step-change');
    if (ch) ch.hidden = false;
  }

  function reopenStep(stepEl) {
    if (!stepEl) return;
    stepEl.classList.remove('is-done', 'is-locked');
    stepEl.classList.add('is-active');
    var sum = stepEl.querySelector('.dctb-step-summary');
    if (sum) sum.hidden = true;
    var ch = stepEl.querySelector('.dctb-step-change');
    if (ch) ch.hidden = true;
  }

  /* Step 1 starts active by default for box-both products. For locked-
     size products (Coquette, Say It) step 1 doesn't exist; dietary step
     becomes the first to activate. */
  function autoActivateStep1() {
    var s1 = $('dctb-step-1');
    if (s1) {
      /* Size step is already active per Liquid markup; nothing to do. */
      return;
    }
    /* No size step → unlock dietary step. */
    var diet = $('dctb-step-dietary');
    if (diet && diet.classList.contains('is-locked')) unlockStep(diet);
  }

  /* Cascading unlock — used after frosting completes. Walks ribbon →
     topper → sprinkles → pearls → filling and unlocks the first one
     whose cfg flag is on and which isn't already done. If justCompleted
     is passed, starts AFTER that step in the chain. */
  function unlockNextAfterFrosting(justCompleted) {
    var order = [
      { id: 'ribbon',    flag: cfg.hasRibbonPicker },
      { id: 'topper',    flag: cfg.hasTopperPerCupcake },
      { id: 'sprinkles', flag: cfg.hasSprinkles },
      { id: 'pearls',    flag: cfg.hasPearls },
      { id: 'filling',   flag: cfg.hasFillingPicker }
    ];
    var startIdx = 0;
    if (justCompleted) {
      for (var i = 0; i < order.length; i++) {
        if (order[i].id === justCompleted) { startIdx = i + 1; break; }
      }
    }
    for (var j = startIdx; j < order.length; j++) {
      if (!order[j].flag) continue;
      var el = $('dctb-step-' + order[j].id);
      if (!el) continue;
      if (el.classList.contains('is-done')) continue;
      unlockStep(el);
      return order[j].id;
    }
    return null;
  }

  /* Hook up the "Change" button + make the whole step head tappable
     when the step is done. Customers were tapping the title bar
     expecting it to expand (natural accordion behavior), but only the
     small "Change" pill responded — that felt broken. Now the entire
     head row reopens the step. We still stop event bubbling so the
     Change button click doesn't double-fire when nested. */
  function bindChangeButtons() {
    $$('.dctb-step-change').forEach(function(btn) {
      on(btn, 'click', function(e) {
        e.preventDefault();
        e.stopPropagation();
        var stepEl = btn.closest('.dctb-step');
        reopenStepWithScroll(stepEl);
      });
    });

    /* Whole-head click → reopen when step is done. Locked + active
       steps don't respond (locked = can't go there yet; active =
       already open, nothing to do). */
    $$('.dctb-step-head').forEach(function(head) {
      on(head, 'click', function(e) {
        var stepEl = head.closest('.dctb-step');
        if (!stepEl) return;
        if (!stepEl.classList.contains('is-done')) return;
        /* Allow clicks on links/inputs inside the head (none today, but
           future-proof). Ignore clicks on the Change button — its own
           handler runs first via stopPropagation above. */
        if (e.target.closest('.dctb-step-change')) return;
        reopenStepWithScroll(stepEl);
      });
      /* Make the head feel tappable on touch devices. CSS already
         handles the cursor via a separate rule. */
      head.style.cursor = 'pointer';
    });
  }

  /* Reopen a step + scroll its title into a comfortable viewport spot.
     Extracted so both the Change button and the head click can share
     the behavior. */
  function reopenStepWithScroll(stepEl) {
    if (!stepEl) return;
    reopenStep(stepEl);
    var titleEl = stepEl.querySelector('.dctb-step-title') || stepEl;
    var top = titleEl.getBoundingClientRect().top + window.pageYOffset - 80;
    window.scrollTo({ top: top, behavior: 'smooth' });
  }


  /* ══════════════════════════════════════════════════════════════
     STEP 1 — SIZE TOGGLE
     ══════════════════════════════════════════════════════════════ */
  function bindSizeCards() {
    $$('.dctb-size-card').forEach(function(btn) {
      on(btn, 'click', function() {
        applySize(parseInt(btn.dataset.size, 10));
      });
    });
  }

  function applySize(newSize) {
    if (newSize !== 6 && newSize !== 12) return;
    state.boxSize = newSize;
    var root = $('dctb-root');
    if (root) root.dataset.boxSize = newSize;

    $$('.dctb-size-card').forEach(function(b) {
      var isMatch = parseInt(b.dataset.size, 10) === newSize;
      b.classList.toggle('is-active', isMatch);
      b.setAttribute('aria-checked', isMatch ? 'true' : 'false');
    });

    var box = $('dctb-box');
    if (box) {
      box.classList.remove('dctb-box--size-6', 'dctb-box--size-12');
      box.classList.add('dctb-box--size-' + newSize);
    }

    /* Flavor slots 3+4 only matter when boxSize === 12. Hide and
       clear their state if downsizing to 6. */
    var s3 = $('dctb-flavor-slot-3');
    var s4 = $('dctb-flavor-slot-4');
    if (s3) s3.hidden = newSize === 6;
    if (s4) s4.hidden = newSize === 6;
    if (newSize === 6) {
      state.flavors[2] = null;
      state.flavors[3] = null;
      renderFlavorSlot(3);
      renderFlavorSlot(4);
    }

    /* Each slot is always 3 cupcakes — only the count of needed slots
       changes (2 for box-6, 4 for box-12). The qty label stays "3
       cupcakes" on every slot. */
    setText('dctb-flavor-sub', '3 cupcakes of each flavor — total ' + newSize);
    [1,2,3,4].forEach(function(n) {
      setText('dctb-flavor-qty-' + n, '3 cupcakes');
    });

    /* GF fee + cap depend on box size — update display values. */
    refreshGfFee();

    refreshSlotsForBoxSize();
    refreshDropZoneLabels();

    /* Clear photos for slots that no longer exist. */
    Object.keys(state.photos).forEach(function(n) {
      if (parseInt(n, 10) > newSize) delete state.photos[n];
    });
    if (state.activeCupcake > newSize) {
      destroyCropper();
      $$('.dctb-box-slot').forEach(function(s) { s.classList.remove('is-active'); });
      state.activeCupcake = 0;
    }

    /* Topper counter (X of N cupcakes used) follows box size, and the
       input's maxlength must match the new cap. Trim any existing value
       that's already over the new cap (downsizing 12 → 6 case). */
    var topperInp = $('dctb-topper-input');
    if (topperInp) {
      topperInp.setAttribute('maxlength', String(newSize));
      if (topperInp.value && topperInp.value.length > newSize) {
        topperInp.value = topperInp.value.substring(0, newSize);
        state.topperRaw = topperInp.value;
      }
    }
    refreshTopperCounter();
    refreshTopperDistribution();

    /* Complete the size step + unlock dietary step. */
    var s1 = $('dctb-step-1');
    if (s1) {
      completeStep(s1, '<span class="dctb-step-summary-text">Box of ' + newSize + '</span>');
    }
    var diet = $('dctb-step-dietary');
    if (diet && diet.classList.contains('is-locked')) unlockStep(diet);

    /* Re-validate flavor completion (slot count changed). */
    refreshFlavorStepCompletion();
    refreshFlavorPrompt();

    updateProgress();
    updateStickyStatus();
  }


  /* ══════════════════════════════════════════════════════════════
     DIETARY STEP — GF + Nut-Free toggles, disclaimers, acks, skip
     New step inserted between size and flavors (2026-05-27).
     ══════════════════════════════════════════════════════════════ */

  function bindGfSwitch() {
    var sw = $('dctb-gf-switch-v2');
    if (!sw) return;
    on(sw, 'click', function(e) {
      e.preventDefault();
      state.gfOn = !state.gfOn;
      sw.classList.toggle('is-on', state.gfOn);
      sw.setAttribute('aria-checked', state.gfOn ? 'true' : 'false');

      /* Reset the ack whenever the toggle flips — turning OFF clears
         the previously-given consent; turning ON requires fresh consent. */
      state.gfAck = false;
      syncAckUi('gf');

      var warning = $('dctb-gf-warning');
      if (warning) warning.hidden = !state.gfOn;
      var rules = $('dctb-gf-rules');
      if (rules) rules.hidden = !state.gfOn;

      /* If turning ON, clear any picked Red Velvet flavors. */
      if (state.gfOn) {
        for (var i = 0; i < 4; i++) {
          var f = state.flavors[i];
          if (f && !isFlavorGfSafe(f)) {
            state.flavors[i] = null;
            renderFlavorSlot(i + 1);
          }
        }
      }
      refreshFlavorCards();
      refreshFlavorStepCompletion();
      refreshDietStepCompletion();
      updateStickyStatus();
    });
    on(sw, 'keydown', function(e) {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); sw.click(); }
    });
  }

  function bindNutSwitch() {
    var sw = $('dctb-nut-switch');
    if (!sw) return;
    on(sw, 'click', function(e) {
      e.preventDefault();
      state.nutOn = !state.nutOn;
      sw.classList.toggle('is-on', state.nutOn);
      sw.setAttribute('aria-checked', state.nutOn ? 'true' : 'false');

      state.nutAck = false;
      syncAckUi('nut');

      var warning = $('dctb-nut-warning');
      if (warning) warning.hidden = !state.nutOn;

      /* If turning ON, clear any picked nut-containing flavors. */
      if (state.nutOn) {
        for (var i = 0; i < 4; i++) {
          var f = state.flavors[i];
          if (f && !isFlavorNutSafe(f)) {
            state.flavors[i] = null;
            renderFlavorSlot(i + 1);
          }
        }
      }
      refreshFlavorCards();
      refreshFlavorStepCompletion();
      refreshDietStepCompletion();
      updateStickyStatus();
    });
    on(sw, 'keydown', function(e) {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); sw.click(); }
    });
  }

  /* Acknowledgment checkbox — sits inside the warning card. Toggling
     it doesn't enable/disable anything else directly; it just gates
     the "Add to cart" action via firstMissing(). */
  function bindGfAck() {
    var ack = $('dctb-gf-ack');
    if (!ack) return;
    on(ack, 'click', function(e) {
      e.preventDefault();
      state.gfAck = !state.gfAck;
      syncAckUi('gf');
      refreshDietStepCompletion();
      updateStickyStatus();
    });
    on(ack, 'keydown', function(e) {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); ack.click(); }
    });
  }

  function bindNutAck() {
    var ack = $('dctb-nut-ack');
    if (!ack) return;
    on(ack, 'click', function(e) {
      e.preventDefault();
      state.nutAck = !state.nutAck;
      syncAckUi('nut');
      refreshDietStepCompletion();
      updateStickyStatus();
    });
    on(ack, 'keydown', function(e) {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); ack.click(); }
    });
  }

  /* Paint the ack checkbox UI. CSS uses [aria-checked="true"] to
     show the green check, so we set both that and a class for safety. */
  function syncAckUi(which) {
    var ackId = which === 'gf' ? 'dctb-gf-ack' : 'dctb-nut-ack';
    var ackEl = $(ackId);
    if (!ackEl) return;
    var on = which === 'gf' ? state.gfAck : state.nutAck;
    ackEl.setAttribute('aria-checked', on ? 'true' : 'false');
    ackEl.classList.toggle('is-checked', on);
  }

  /* The dietary step is OPTIONAL. Completing it means either:
     - both toggles off + customer hit "skip", OR
     - any toggle on + that toggle's ack is checked.
     We don't auto-complete on first toggle interaction — the customer
     might still be deciding. completeStep is only called from the skip
     pill OR from refreshDietStepCompletion() once acks are satisfied. */
  function refreshDietStepCompletion() {
    var stepEl = $('dctb-step-dietary');
    if (!stepEl) return;

    /* Both off → step stays open until they hit skip. */
    if (!state.gfOn && !state.nutOn) {
      /* If previously completed, just re-open. */
      if (stepEl.classList.contains('is-done')) reopenStep(stepEl);
      return;
    }

    /* Any toggle on requires its ack. If acks aren't satisfied yet,
       leave step open. */
    if (state.gfOn && !state.gfAck) {
      if (stepEl.classList.contains('is-done')) reopenStep(stepEl);
      return;
    }
    if (state.nutOn && !state.nutAck) {
      if (stepEl.classList.contains('is-done')) reopenStep(stepEl);
      return;
    }

    /* Auto-complete + unlock flavors. */
    var bits = [];
    if (state.gfOn) bits.push('Gluten-free');
    if (state.nutOn) bits.push('Nut-free');
    var summary = '<span class="dctb-step-summary-text">' + escapeHtml(bits.join(' + ')) + '</span>';
    completeStep(stepEl, summary);
    var flav = $('dctb-step-2');
    if (flav && flav.classList.contains('is-locked')) unlockStep(flav);
  }

  function bindDietSkip() {
    var skip = $('dctb-diet-skip');
    if (!skip) return;
    on(skip, 'click', function(e) {
      e.preventDefault();
      /* Force-clear any partial state. The skip pill is a "no dietary
         needs" statement, so flipping a toggle then skipping should
         clear that toggle rather than confuse the cart. */
      if (state.gfOn) {
        state.gfOn = false;
        state.gfAck = false;
        var gfSw = $('dctb-gf-switch-v2');
        if (gfSw) {
          gfSw.classList.remove('is-on');
          gfSw.setAttribute('aria-checked', 'false');
        }
        var gfWarn = $('dctb-gf-warning');
        if (gfWarn) gfWarn.hidden = true;
        var gfRules = $('dctb-gf-rules');
        if (gfRules) gfRules.hidden = true;
        syncAckUi('gf');
      }
      if (state.nutOn) {
        state.nutOn = false;
        state.nutAck = false;
        var nSw = $('dctb-nut-switch');
        if (nSw) {
          nSw.classList.remove('is-on');
          nSw.setAttribute('aria-checked', 'false');
        }
        var nWarn = $('dctb-nut-warning');
        if (nWarn) nWarn.hidden = true;
        syncAckUi('nut');
      }
      refreshFlavorCards();
      var stepEl = $('dctb-step-dietary');
      if (stepEl) {
        completeStep(stepEl, '<span class="dctb-step-summary-text">No dietary needs</span>');
      }
      var flav = $('dctb-step-2');
      if (flav && flav.classList.contains('is-locked')) unlockStep(flav);
      updateStickyStatus();
    });
  }

  function refreshGfFee() {
    var gfByBox = cfg.gfByBoxSize || {};
    var box = state.boxSize === 12 ? gfByBox.box12 : gfByBox.box6;
    if (!box) return;
    setText('dctb-gf-fee-display', box.display);
    setText('dctb-gf-cap-display', String(box.flavorCap));
    setText('dctb-gf-cap-display-2', String(box.flavorCap));
  }

  /* Helpers — look up gf/nut safety for a flavor name. The cfg.flavors
     array (from Liquid) carries gfSafe + nutSafe per row. We could also
     read straight from the card's data-* attrs, but cfg.flavors lets us
     check by name (e.g. when restoring state). */
  function isFlavorGfSafe(name) {
    var list = cfg.flavors || [];
    for (var i = 0; i < list.length; i++) {
      if (list[i].name === name) return list[i].gfSafe !== false;
    }
    return true;
  }
  function isFlavorNutSafe(name) {
    var list = cfg.flavors || [];
    for (var i = 0; i < list.length; i++) {
      if (list[i].name === name) return list[i].nutSafe !== false;
    }
    return true;
  }


  /* ══════════════════════════════════════════════════════════════
     FLAVOR SLOTS + CARDS
     Replaced <select> dropdowns with a card-based picker (2026-05-27).
     UX: tap slot → it becomes active → tap any card → fills that
     slot → auto-advance to next empty slot. Slot 1 starts active.
     ══════════════════════════════════════════════════════════════ */

  function bindFlavorSlots() {
    /* Slot row + slot button both activate the slot when tapped. The
       row's button takes the click; we just need to wire it. */
    $$('.dctb-flavor-slot-btn').forEach(function(btn) {
      on(btn, 'click', function(e) {
        e.preventDefault();
        var slotEl = btn.closest('.dctb-flavor-slot');
        if (!slotEl) return;
        var n = parseInt(slotEl.dataset.flavorSlot, 10);
        activateFlavorSlot(n);
      });
    });
  }

  function activateFlavorSlot(n) {
    if (n < 1 || n > 4) return;
    if (n > 2 && state.boxSize === 6) return;
    state.activeFlavorSlot = n;
    $$('.dctb-flavor-slot').forEach(function(s) {
      var match = parseInt(s.dataset.flavorSlot, 10) === n;
      s.classList.toggle('is-active', match);
    });
    refreshFlavorCards();
    refreshFlavorPrompt();
    scrollFlavorGridIntoView();
  }

  /* When a slot becomes active, bring the flavor picker grid into view.
     The slots sit stacked above the (single, shared) picker grid, so on
     mobile a customer who taps a slot would otherwise have to scroll down
     to find the flavors — they're disconnected. Pulling the grid into the
     viewport closes that gap. Only nudges on smaller screens (the desktop
     layout shows both at once). rAF lets the prompt text update first. */
  function scrollFlavorGridIntoView() {
    if (window.innerWidth > 749) return;
    var grid = $('dctb-flavor-grid-wrap') ||
               (function(){ var g = $$('.dctb-flavor-grid-wrap')[0]; return g; })();
    if (!grid) return;
    var doScroll = function() {
      var r = grid.getBoundingClientRect();
      /* Only scroll if the grid isn't already comfortably in view — avoids
         a jarring jump when it's already visible (e.g. re-tapping a slot). */
      if (r.top < 70 || r.bottom > window.innerHeight) {
        var top = r.top + window.pageYOffset - 90;
        window.scrollTo({ top: top, behavior: 'smooth' });
      }
    };
    if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(doScroll);
    } else {
      doScroll();
    }
  }

  /* Update the "Pick Flavor N" prompt above the card grid based on the
     currently-active slot. */
  function refreshFlavorPrompt() {
    var n = state.activeFlavorSlot;
    setText('dctb-flavor-grid-prompt', '↓ Tap a flavor for slot ' + n);
  }

  function bindFlavorCards() {
    $$('.dctb-flavor-card').forEach(function(card) {
      on(card, 'click', function(e) {
        e.preventDefault();
        /* Hidden-by-diet-toggle cards stay un-tappable. is-hidden is set
           by refreshFlavorCards when GF/Nut-Free filters apply. We no
           longer block duplicate picks — customer can put the same
           flavor in all 4 slots if they want. */
        if (card.classList.contains('is-hidden')) return;
        var name = card.dataset.flavorName;
        var n = state.activeFlavorSlot || 1;
        state.flavors[n - 1] = name;
        renderFlavorSlot(n);

        /* Auto-advance: find the next empty slot WITHIN the needed
           count for this box size. If all needed slots are filled,
           don't change activeFlavorSlot (customer can still re-tap
           an existing slot to swap). */
        var needed = flavorsRequired();
        var nextEmpty = null;
        for (var i = 1; i <= needed; i++) {
          if (!state.flavors[i - 1]) { nextEmpty = i; break; }
        }
        if (nextEmpty) {
          activateFlavorSlot(nextEmpty);
        } else {
          refreshFlavorCards();
        }

        refreshFlavorStepCompletion();
        updateStickyStatus();
      });
      on(card, 'keydown', function(e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); card.click(); }
      });
    });
  }

  /* Paint a single slot's filled/empty state. */
  function renderFlavorSlot(n) {
    var slotEl = $('dctb-flavor-slot-' + n);
    if (!slotEl) return;
    var btn = $('dctb-flavor-slot-btn-' + n);
    if (!btn) return;
    var emptyEl = btn.querySelector('.dctb-flavor-slot-empty');
    var filledEl = btn.querySelector('.dctb-flavor-slot-filled');
    var nameEl = $('dctb-flavor-slot-name-' + n);
    var flavor = state.flavors[n - 1];
    if (flavor) {
      slotEl.classList.add('is-filled');
      if (emptyEl) emptyEl.hidden = true;
      if (filledEl) filledEl.hidden = false;
      if (nameEl) nameEl.textContent = flavor;
    } else {
      slotEl.classList.remove('is-filled');
      if (emptyEl) emptyEl.hidden = false;
      if (filledEl) filledEl.hidden = true;
      if (nameEl) nameEl.textContent = '';
    }
  }

  /* Filter visible flavor cards by:
     - GF on → hide flavors with data-gf-safe="false"
     - Nut-Free on → hide flavors with data-nut-safe="false"
     - mark cards already used in OTHER slots as locked (visual only,
       customer can still tap one to swap into the active slot)
     Cards never get fully removed — they just hide via .is-hidden class.
     CSS .is-hidden { display: none !important; } handles that. */
  function refreshFlavorCards() {
    var activeSlot = state.activeFlavorSlot || 1;
    $$('.dctb-flavor-card').forEach(function(card) {
      var gfSafe = card.dataset.gfSafe !== 'false';
      var nutSafe = card.dataset.nutSafe !== 'false';
      var name = card.dataset.flavorName;

      var hideForGf = state.gfOn && !gfSafe;
      var hideForNut = state.nutOn && !nutSafe;

      card.classList.toggle('is-hidden', hideForGf || hideForNut);

      /* Currently-picked in the ACTIVE slot → mark selected. We deliberately
         do NOT lock cards that are picked in OTHER slots — customers can put
         the same flavor in all 4 slots (e.g. "I want all Vanilla"). */
      var isSelectedHere = state.flavors[activeSlot - 1] === name;
      card.classList.toggle('is-selected', isSelectedHere);
      card.setAttribute('aria-checked', isSelectedHere ? 'true' : 'false');
    });
  }

  function flavorsRequired() {
    return state.boxSize === 12 ? 4 : 2;
  }

  function chosenFlavors() {
    var n = flavorsRequired();
    var out = [];
    for (var i = 0; i < n; i++) if (state.flavors[i]) out.push(state.flavors[i]);
    return out;
  }

  function refreshFlavorStepCompletion() {
    var s2 = $('dctb-step-2');
    if (!s2) return;
    var chosen = chosenFlavors();
    if (chosen.length < flavorsRequired()) {
      if (s2.classList.contains('is-done')) reopenStep(s2);
      return;
    }
    var flavorList = chosen.map(function(f) { return shortFlavorName(f); }).join(', ');
    var summary = '<span class="dctb-step-summary-text">' + escapeHtml(flavorList) + '</span>';
    if (state.gfOn) summary += ' <span class="dctb-step-summary-gf">GLUTEN-FREE</span>';
    completeStep(s2, summary);

    /* Unlock the next applicable step. For rainbow + peter-rabbit
       there's no frosting step, so skip straight to ribbon/topper/etc. */
    var s3 = $('dctb-step-3');
    if (s3) {
      unlockStep(s3);
    } else {
      unlockNextAfterFrosting();
    }
  }

  /* Shorten verbose flavor names for the summary line so 4 flavors
     fit on one row. "Vanilla Cupcake filled with Dulce de Leche" →
     "Vanilla · Dulce de Leche". */
  function shortFlavorName(full) {
    if (!full) return '';
    return full
      .replace(/\s*Cupcake\s+filled\s+with\s+/i, ' · ')
      .replace(/\s*Cupcake\s*$/i, '')
      .replace(/\s+/g, ' ')
      .trim();
  }


  /* ══════════════════════════════════════════════════════════════
     FROSTING COLOR (palette / gender-reveal / locked)
     ══════════════════════════════════════════════════════════════ */
  function bindFrostingSwatches() {
    $$('#dctb-step-3 .dctb-frosting-swatch:not([data-second])').forEach(function(btn) {
      on(btn, 'click', function() { selectFrosting(btn); });
    });
  }

  function selectFrosting(btn) {
    var name = btn.dataset.colorName;
    var hex = btn.dataset.colorHex;
    var isLight = btn.dataset.isLight === 'true';
    var isCustom = btn.dataset.isCustom === 'true';

    state.frostingColor = name;
    state.frostingHex = hex;
    state.frostingIsLight = isLight;
    state.frostingIsCustom = isCustom;
    state.customColorFee = isCustom ? 10 : 0;

    $$('#dctb-step-3 .dctb-frosting-swatch:not([data-second])').forEach(function(b) {
      var isMatch = b === btn;
      b.classList.toggle('is-active', isMatch);
      b.setAttribute('aria-checked', isMatch ? 'true' : 'false');
    });

    var picker = $('dctb-custom-color-wrap');
    if (picker) picker.classList.toggle('is-visible', isCustom);
    if (isCustom) {
      var pickerInput = $('dctb-custom-color-input');
      if (pickerInput) state.frostingHex = pickerInput.value;
    }

    applyFrostingToPreview();
    refreshFrostingStepCompletion();
    updateStickyStatus();
  }

  function bindCustomColorPicker() {
    var input = $('dctb-custom-color-input');
    if (!input) return;
    on(input, 'input', function() {
      var hex = input.value;
      state.frostingHex = hex;
      var prev = $('dctb-custom-color-preview');
      var hexEl = $('dctb-custom-color-hex');
      if (prev) prev.style.background = hex;
      if (hexEl) hexEl.textContent = hex;
      applyFrostingToPreview();
      refreshFrostingStepCompletion();
    });
  }

  function bindTwoToneToggle() {
    var btn = $('dctb-twotone-toggle');
    if (!btn) return;
    on(btn, 'click', function(e) {
      e.preventDefault();
      state.twoToneOn = !state.twoToneOn;
      btn.classList.toggle('is-on', state.twoToneOn);
      btn.setAttribute('aria-pressed', state.twoToneOn ? 'true' : 'false');
      var panel = $('dctb-twotone-active');
      if (panel) panel.hidden = !state.twoToneOn;
      if (!state.twoToneOn) {
        state.secondColor = null;
        state.secondHex = null;
        $$('[data-second="true"]').forEach(function(s) {
          s.classList.remove('is-active');
          s.setAttribute('aria-checked', 'false');
        });
      }
      refreshFrostingStepCompletion();
    });
  }

  function bindSecondFrosting() {
    $$('[data-second="true"]').forEach(function(btn) {
      on(btn, 'click', function() {
        state.secondColor = btn.dataset.colorName;
        state.secondHex = btn.dataset.colorHex;
        state.secondIsLight = btn.dataset.isLight === 'true';
        $$('[data-second="true"]').forEach(function(b) {
          var isMatch = b === btn;
          b.classList.toggle('is-active', isMatch);
          b.setAttribute('aria-checked', isMatch ? 'true' : 'false');
        });
        refreshFrostingStepCompletion();
      });
    });
  }

  function applyTwoToneDefaults() {
    if (!cfg.twoToneRequired) return;
    state.twoToneOn = true;
    if (cfg.twoToneDefaultFirstName) {
      state.frostingColor = cfg.twoToneDefaultFirstName;
      state.frostingHex = cfg.twoToneDefaultFirstHex;
      state.frostingIsLight = isLightHex(cfg.twoToneDefaultFirstHex);
    }
    if (cfg.twoToneDefaultSecondName) {
      state.secondColor = cfg.twoToneDefaultSecondName;
      state.secondHex = cfg.twoToneDefaultSecondHex;
      state.secondIsLight = isLightHex(cfg.twoToneDefaultSecondHex);
    }
    applyFrostingToPreview();
  }

  function applyFrostingToPreview() {
    var hex = state.frostingHex;
    if (!hex) return;
    if (state.twoToneOn && state.secondHex) {
      var grad = 'linear-gradient(135deg, ' + hex + ' 0%, ' + hex + ' 50%, ' + state.secondHex + ' 50%, ' + state.secondHex + ' 100%)';
      $$('.dctb-box-slot-frosting').forEach(function(el) { el.style.background = grad; });
    } else {
      $$('.dctb-box-slot-frosting').forEach(function(el) { el.style.background = hex; });
    }

    var sw = $('dctb-preview-frosting-swatch');
    var nm = $('dctb-preview-frosting-name');
    if (sw) {
      sw.style.background = state.twoToneOn && state.secondHex
        ? 'linear-gradient(135deg,' + hex + ' 50%,' + state.secondHex + ' 50%)'
        : hex;
      sw.style.borderColor = state.frostingIsLight ? 'rgba(0,0,0,0.18)' : 'rgba(0,0,0,0.1)';
    }
    if (nm) {
      var label = state.frostingIsCustom ? ('Custom ' + hex) : state.frostingColor;
      if (state.twoToneOn && state.secondColor) label += ' + ' + state.secondColor;
      nm.textContent = label || '—';
    }
  }

  function refreshFrostingStepCompletion() {
    var s3 = $('dctb-step-3');
    if (!s3) return;
    if (!state.frostingColor) return;
    if (cfg.twoToneRequired && !state.secondColor) return;
    if (cfg.hasTwoTone && state.twoToneOn && !state.secondColor) return;

    var label;
    if (state.frostingIsCustom) {
      label = 'Custom ' + state.frostingHex + ' (+$10)';
    } else {
      label = state.frostingColor;
    }
    if (state.twoToneOn && state.secondColor) {
      label += ' + ' + state.secondColor;
    }
    var dotStyle = 'background:' + state.frostingHex + ';';
    if (state.frostingIsLight) dotStyle += 'border:1px solid rgba(0,0,0,0.18);';
    var html = '<span class="dctb-step-summary-dot" style="' + dotStyle + '"></span>'
             + '<span class="dctb-step-summary-text">' + escapeHtml(label) + '</span>';

    completeStep(s3, html);
    unlockNextAfterFrosting();
  }


  /* ══════════════════════════════════════════════════════════════
     RIBBON (Coquette)
     ══════════════════════════════════════════════════════════════ */
  function bindRibbonCells() {
    $$('.dctb-ribbon-cell').forEach(function(cell) {
      on(cell, 'click', function(e) {
        e.preventDefault();
        var name = cell.dataset.ribbonName;
        var hex = cell.dataset.ribbonHex;
        state.ribbonName = name;
        state.ribbonHex = hex;

        if (cfg.topperColorSource === 'ribbon') {
          state.topperColorName = name;
          state.topperColorHex = hex;
        }
        var root = $('dctb-root');
        if (root) root.style.setProperty('--dctb-ribbon-color', hex);

        $$('.dctb-ribbon-cell').forEach(function(c) {
          c.classList.toggle('is-active', c === cell);
          c.setAttribute('aria-checked', c === cell ? 'true' : 'false');
        });
        var msg = $('dctb-ribbon-active-msg');
        var nameEl = $('dctb-ribbon-active-name');
        if (msg) msg.hidden = false;
        if (nameEl) nameEl.textContent = name;

        var stepEl = $('dctb-step-ribbon');
        if (stepEl) {
          var html = '<span class="dctb-step-summary-dot" style="background:' + hex + ';"></span>'
                   + '<span class="dctb-step-summary-text">' + escapeHtml(name) + ' ribbon</span>';
          completeStep(stepEl, html);
        }
        refreshTopperDistribution();
        unlockNextAfterFrosting('ribbon');
        updateStickyStatus();
      });
    });
  }

  /* ── "Swipe for more colors →" hint on scrollable color rows ──
     Both the ribbon color row and the frosting color row scroll
     horizontally on mobile. This injects a small fading hint above a
     given grid when it actually overflows, and fades it out on first
     scroll. Mirrors the heart cake builder. Idempotent — safe to call
     repeatedly; it no-ops if the row isn't visible/overflowing or if a
     hint is already there. */
  function addSwipeHintTo(grid) {
    if (!grid) return;

    /* Only relevant at mobile widths where the row scrolls. */
    if (window.innerWidth > 749) return;

    /* Nothing to swipe if it all fits on screen (also catches the
       display:none case where scrollWidth/clientWidth are both 0). */
    if (grid.scrollWidth <= grid.clientWidth + 4) return;

    /* Don't double-inject. */
    var prev = grid.previousElementSibling;
    if (prev && prev.classList && prev.classList.contains('dctb-swipe-hint')) return;

    var hint = document.createElement('div');
    hint.className = 'dctb-swipe-hint';
    hint.setAttribute('aria-hidden', 'true');
    hint.innerHTML = 'Swipe for more colors <span>&rarr;</span>';
    grid.parentNode.insertBefore(hint, grid);

    /* Fade out the first time they scroll the row sideways. */
    var faded = false;
    on(grid, 'scroll', function() {
      if (faded) return;
      if (grid.scrollLeft > 8) {
        faded = true;
        hint.classList.add('is-hidden');
      }
    });
  }

  function initRibbonSwipeHint() {
    /* Ribbon color row. */
    addSwipeHintTo($$('.dctb-ribbon-grid')[0]);
    /* Frosting color row — the main picker lives in step 3. Target that
       specific grid so we don't hint the two-tone/topper sub-grids. */
    var step3 = $('dctb-step-3');
    if (step3) {
      addSwipeHintTo(step3.querySelector('.dctb-frosting-grid'));
    }
  }


  /* ══════════════════════════════════════════════════════════════
     PER-CUPCAKE TOPPER (Coquette + Say It)
     ══════════════════════════════════════════════════════════════ */
  /* Allowed topper icons — collected from the quick-insert buttons at
     bind time, so whatever the two icons are in the Liquid, they're
     permitted. Letters (A–Z) and digits (0–9) are always allowed.
     Everything else (punctuation, emoji, @#$, etc.) is stripped as the
     customer types, since the bakery can only pipe letters, numbers,
     and the offered icons onto the cupcakes. */
  var ALLOWED_TOPPER_ICONS = [];

  function sanitizeTopper(raw) {
    if (!raw) return '';
    var out = '';
    /* Iterate by code point so multi-byte icons/emoji aren't split. */
    var chars = Array.from(String(raw));
    for (var i = 0; i < chars.length; i++) {
      var ch = chars[i];
      if (/[A-Za-z0-9]/.test(ch)) { out += ch; continue; }
      if (/\s/.test(ch)) { out += ch; continue; } /* keep spaces; stripped later in distribute */
      if (ALLOWED_TOPPER_ICONS.indexOf(ch) !== -1) { out += ch; continue; }
      /* anything else is dropped */
    }
    return out;
  }

  function bindTopperInput() {
    var inp = $('dctb-topper-input');
    if (!inp) return;

    /* Build the allowed-icon list from the quick-insert buttons. */
    ALLOWED_TOPPER_ICONS = [];
    $$('.dctb-topper-quick-btn').forEach(function(b) {
      if (b.dataset.topperInsert) {
        Array.from(String(b.dataset.topperInsert)).forEach(function(ch) {
          if (ALLOWED_TOPPER_ICONS.indexOf(ch) === -1) ALLOWED_TOPPER_ICONS.push(ch);
        });
      }
    });

    on(inp, 'input', function() {
      /* Strip any disallowed characters the moment they're typed/pasted.
         Preserve caret position so filtering mid-string doesn't jump the
         cursor to the end. */
      var clean = sanitizeTopper(inp.value);
      if (clean !== inp.value) {
        var pos = inp.selectionStart;
        var removed = inp.value.length - clean.length;
        inp.value = clean;
        var newPos = Math.max(0, (pos || clean.length) - removed);
        try { inp.selectionStart = inp.selectionEnd = newPos; } catch (e) {}
      }
      state.topperRaw = inp.value;
      refreshTopperDistribution();
      refreshTopperStepCompletion();
      updateStickyStatus();
    });

    $$('.dctb-topper-quick-btn').forEach(function(b) {
      if (b.dataset.topperInsert) {
        on(b, 'click', function(e) {
          e.preventDefault();
          /* Respect the input's maxlength when inserting symbols via
             quick-action buttons. The browser enforces maxlength only
             on typing/paste — programmatic value changes bypass it. */
          var cap = parseInt(inp.getAttribute('maxlength'), 10) || state.boxSize;
          if (inp.value.length >= cap) return;
          var insertChar = b.dataset.topperInsert;
          insertAtCursor(inp, insertChar);
          /* Truncate defensively in case selection-replace pushed length
             over the cap (shouldn't happen given the check above, but
             belt-and-suspenders for edge cases like RTL pastes). */
          if (inp.value.length > cap) inp.value = inp.value.substring(0, cap);
          state.topperRaw = inp.value;
          refreshTopperDistribution();
          refreshTopperStepCompletion();
          updateStickyStatus();
        });
      } else if (b.dataset.topperAction === 'clear') {
        on(b, 'click', function(e) {
          e.preventDefault();
          inp.value = '';
          state.topperRaw = '';
          refreshTopperDistribution();
          refreshTopperStepCompletion();
          updateStickyStatus();
        });
      }
    });
  }

  function insertAtCursor(input, text) {
    if (typeof input.selectionStart === 'number') {
      var start = input.selectionStart, end = input.selectionEnd;
      input.value = input.value.slice(0, start) + text + input.value.slice(end);
      input.selectionStart = input.selectionEnd = start + text.length;
    } else {
      input.value += text;
    }
    input.focus();
  }

  /* Distribute the topper message into per-cupcake chunks.
     Rules (2026-05-27 — simplified to 1 char per cupcake):
       - Each character = exactly one cupcake
       - Only letters, numbers, and the offered icons are allowed;
         anything else is sanitized out (sanitizeTopper) — this is a
         backstop in case state.topperRaw was set outside the input
         handler.
       - Whitespace is stripped (HAPPY BDAY → HAPPYBDAY = 9 cupcakes)
       - Output is always UPPERCASE. Customer can type either case;
         the bakery always prints capitals on the cupcakes. CooperBlack
         is rendered as caps; lowercase glyphs would look inconsistent.
         The offered icons pass through unchanged.
       - Hard-cap at boxSize. The input's maxlength enforces this too,
         but we slice() defensively so a paste or programmatic value
         that exceeds the cap still gets trimmed before painting. */
  function distributeTopper(raw, boxSize) {
    if (!raw) return [];
    var clean = sanitizeTopper(raw);
    var stripped = String(clean).replace(/\s+/g, '').toUpperCase();
    var chunks = [];
    /* Count by code point so multi-byte icons aren't split. */
    var chars = Array.from(stripped);
    for (var i = 0; i < chars.length && i < boxSize; i++) {
      chunks.push(chars[i]);
    }
    return chunks;
  }

  function refreshTopperDistribution() {
    state.topperChunks = distributeTopper(state.topperRaw, state.boxSize);

    var root = $('dctb-root');
    if (root) {
      if (state.topperRaw && state.topperRaw.trim().length > 0) {
        root.classList.add('has-topper-content');
      } else {
        root.classList.remove('has-topper-content');
      }
    }

    var color = state.topperColorHex || '#3a1a50';
    if (root) root.style.setProperty('--dctb-topper-color', color);

    for (var n = 1; n <= 12; n++) {
      var slot = $('dctb-box-slot-' + n);
      if (!slot) continue;
      var top = $('dctb-box-slot-top-' + n);
      if (!top) continue;
      var existing = top.querySelector('.dctb-box-slot-topper');
      var chunk = state.topperChunks[n - 1];
      if (chunk) {
        slot.setAttribute('data-has-topper', '1');
        if (existing) {
          existing.textContent = chunk;
        } else {
          var span = document.createElement('span');
          span.className = 'dctb-box-slot-topper';
          span.textContent = chunk;
          top.appendChild(span);
        }
      } else {
        slot.removeAttribute('data-has-topper');
        if (existing) existing.remove();
      }
    }

    refreshTopperCounter();
  }

  function refreshTopperCounter() {
    var counter = $('dctb-topper-counter');
    if (counter) {
      counter.textContent = state.topperChunks.length + ' of ' + state.boxSize + ' cupcakes used';
    }
  }

  /* Topper "completion" is intentionally NOT marked while the customer
     is editing. Collapsing the step every keystroke would hide the
     input as soon as they type a single character — terrible UX. So:
       - We never call completeStep() from the input handlers
       - We DO unlock the next downstream step (sprinkles/pearls/filling)
         the moment the message becomes valid, so customers can keep
         scrolling without losing access to remaining steps
       - The step only collapses when the customer revisits it via the
         Change button on a later step, or never (it just stays open)
     Cart submit gating happens via firstMissing() which checks for
     state.topperChunks.length === state.boxSize and the letter color
     requirement. */
  function refreshTopperStepCompletion() {
    var stepEl = $('dctb-step-topper');
    if (!stepEl) return;
    if (state.topperChunks.length === 0) return;
    /* Only unlock downstream once the message fills every cupcake AND
       the letter color is satisfied (for palette-mode products). */
    if (state.topperChunks.length < state.boxSize) return;
    if (cfg.topperColorSource === 'palette' && !state.topperColorName) return;
    unlockNextAfterFrosting('topper');
  }

  function bindTopperColorBtn() {
    var btn = $('dctb-topper-color-btn');
    if (!btn) return;
    on(btn, 'click', function(e) {
      e.preventDefault();
      var grid = $('dctb-topper-color-grid');
      if (grid) grid.hidden = !grid.hidden;
    });
  }

  function bindTopperColorGrid() {
    $$('#dctb-topper-color-grid .dctb-frosting-swatch').forEach(function(sw) {
      on(sw, 'click', function() {
        var name = sw.dataset.topperColorName;
        var hex = sw.dataset.topperColorHex;
        state.topperColorName = name;
        state.topperColorHex = hex;

        $$('#dctb-topper-color-grid .dctb-frosting-swatch').forEach(function(s) {
          var isMatch = s === sw;
          s.classList.toggle('is-active', isMatch);
          s.setAttribute('aria-checked', isMatch ? 'true' : 'false');
        });

        var swEl = $('dctb-topper-letter-swatch');
        var nmEl = $('dctb-topper-letter-name');
        if (swEl) swEl.style.background = hex;
        if (nmEl) nmEl.textContent = name;

        var grid = $('dctb-topper-color-grid');
        if (grid) grid.hidden = true;

        refreshTopperDistribution();
        refreshTopperStepCompletion();
      });
    });
  }


  /* ══════════════════════════════════════════════════════════════
     SPRINKLES
     ══════════════════════════════════════════════════════════════ */
  function bindSprinkleCards() {
    $$('.dctb-sprinkle-card').forEach(function(card) {
      on(card, 'click', function(e) {
        e.preventDefault();
        var key = card.dataset.sprinkleKey;
        var name = card.dataset.sprinkleName;
        var price = parseInt(card.dataset.sprinklePrice, 10) || 0;
        state.sprinkleKey = key;
        state.sprinkleName = name;
        state.sprinklePrice = price;
        state.sprinkleIsPearl = /pearl/i.test(key);

        $$('.dctb-sprinkle-card').forEach(function(c) {
          var isMatch = c === card;
          c.classList.toggle('is-active', isMatch);
          c.setAttribute('aria-checked', isMatch ? 'true' : 'false');
        });

        var stepEl = $('dctb-step-sprinkles');
        if (stepEl) {
          var summary = escapeHtml(name);
          if (price > 0) summary += ' · +$' + (price / 100).toFixed(2);
          completeStep(stepEl, '<span class="dctb-step-summary-text">' + summary + '</span>');
        }
        unlockNextAfterFrosting('sprinkles');
        updateStickyStatus();
      });
    });
  }


  /* ══════════════════════════════════════════════════════════════
     PEARLS (Butterfly)
     ══════════════════════════════════════════════════════════════ */
  function bindPearlCards() {
    $$('.dctb-pearl-card').forEach(function(card) {
      on(card, 'click', function(e) {
        e.preventDefault();
        var key = card.dataset.pearlKey;
        var name = card.dataset.pearlName;
        state.pearlKey = key;
        state.pearlName = name;

        $$('.dctb-pearl-card').forEach(function(c) {
          var isMatch = c === card;
          c.classList.toggle('is-active', isMatch);
          c.setAttribute('aria-checked', isMatch ? 'true' : 'false');
        });

        var stepEl = $('dctb-step-pearls');
        if (stepEl) {
          completeStep(stepEl, '<span class="dctb-step-summary-text">' + escapeHtml(name) + '</span>');
        }
        unlockNextAfterFrosting('pearls');
        updateStickyStatus();
      });
    });
  }


  /* ══════════════════════════════════════════════════════════════
     FILLING (Gender Reveal)
     ══════════════════════════════════════════════════════════════ */
  function bindFillingCards() {
    $$('.dctb-filling-card').forEach(function(card) {
      on(card, 'click', function(e) {
        e.preventDefault();
        var f = card.dataset.filling;
        state.fillingChoice = f;
        $$('.dctb-filling-card').forEach(function(c) {
          var isMatch = c === card;
          c.classList.toggle('is-active', isMatch);
          c.setAttribute('aria-checked', isMatch ? 'true' : 'false');
        });
        var stepEl = $('dctb-step-filling');
        if (stepEl) {
          var label = f === 'pink' ? 'Pink filling — it\'s a girl 💖' : 'Blue filling — it\'s a boy 💙';
          completeStep(stepEl, '<span class="dctb-step-summary-text">' + escapeHtml(label) + '</span>');
        }
        updateStickyStatus();
      });
    });
  }


  /* ══════════════════════════════════════════════════════════════
     MAKE IT SPECIAL — addon cards (gift message + candles)
     ══════════════════════════════════════════════════════════════ */

  function bindAddonRows() {
    $$('.dctb-addon-row').forEach(function(row) {
      on(row, 'click', function() {
        var card = row.closest('.dctb-addon-card');
        if (!card) return;
        var nowActive = !card.classList.contains('is-active');
        card.classList.toggle('is-active', nowActive);
        row.setAttribute('aria-expanded', String(nowActive));
        var panelId = row.getAttribute('aria-controls');
        if (panelId) {
          var panel = document.getElementById(panelId);
          if (panel) panel.hidden = !nowActive;
        }
        updateStickyStatus();
      });
      on(row, 'keydown', function(e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); row.click(); }
      });
    });
  }

  function bindTypePills() {
    $$('.dctb-type-pill').forEach(function(pill) {
      on(pill, 'click', function() {
        var on = pill.getAttribute('aria-checked') !== 'true';
        pill.setAttribute('aria-checked', String(on));
        pill.classList.toggle('is-active', on);
        var type = pill.dataset.type;
        var sub = $('dctb-candle-sub-' + type);
        if (sub) sub.hidden = !on;
        updateStickyStatus();
      });
    });
  }

  function bindImgTiles() {
    $$('.dctb-img-tile').forEach(function(tile) {
      on(tile, 'click', function() {
        var group = tile.dataset.tileGroup;
        if (!group) return;
        $$('.dctb-img-tile[data-tile-group="' + group + '"]').forEach(function(t) {
          t.classList.remove('is-selected');
          t.setAttribute('aria-checked', 'false');
        });
        tile.classList.add('is-selected');
        tile.setAttribute('aria-checked', 'true');
        updateStickyStatus();
      });
      on(tile, 'keydown', function(e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); tile.click(); }
      });
    });
  }

  function bindNumberCandleInput() {
    var numInput = $('dctb-candle-number');
    if (!numInput) return;
    on(numInput, 'input', function() {
      updateStickyStatus();
    });
  }

  function bindGiftTextarea() {
    var ta = $('dctb-gift-text');
    if (!ta) return;
    on(ta, 'input', function() {
      updateStickyStatus();
    });
  }

  function isCandleCardActive() {
    var card = $('dctb-addon-card-candles');
    return !!(card && card.classList.contains('is-active'));
  }
  function isPillOn(type) {
    var p = document.querySelector('.dctb-type-pill[data-type="' + type + '"]');
    return p && p.getAttribute('aria-checked') === 'true';
  }
  function selectedTile(subPanelId) {
    return document.querySelector('#' + subPanelId + ' .dctb-img-tile.is-selected');
  }
  function numberCandleDigits() {
    var inp = $('dctb-candle-number');
    if (!inp || !inp.value) return '';
    return String(inp.value).replace(/\D/g, '');
  }
  function giftCardActive() {
    var card = $('dctb-addon-card-gift');
    return !!(card && card.classList.contains('is-active'));
  }
  function giftMessageText() {
    var ta = $('dctb-gift-text');
    return ta ? ta.value.trim() : '';
  }

  function candleSummaryPieces() {
    if (!isCandleCardActive()) return [];
    var pieces = [];
    if (isPillOn('rainbow'))   pieces.push('Rainbow candles');
    if (isPillOn('sparklers')) pieces.push('Sparklers');
    if (isPillOn('metallic')) {
      var mTile = selectedTile('dctb-candle-sub-metallic');
      if (mTile) {
        var mColor = mTile.dataset.tileValue || '';
        pieces.push('Metallic candles' + (mColor ? ' (' + mColor + ')' : ''));
      }
    }
    if (isPillOn('number')) {
      var nTile = selectedTile('dctb-candle-sub-number');
      var nDigits = numberCandleDigits();
      if (nTile && nDigits) {
        var nColor = nTile.dataset.tileValue || '';
        pieces.push('Number candles' + (nColor ? ' (' + nColor + ')' : '') + ' — #' + nDigits);
      }
    }
    return pieces;
  }


  /* ══════════════════════════════════════════════════════════════
     PHOTO UPLOAD — per-cupcake AND shared-logo branches
     ══════════════════════════════════════════════════════════════ */
  function refreshSlotsForBoxSize() {
    $$('.dctb-box-slot').forEach(function(slot) {
      var n = parseInt(slot.dataset.slot, 10);
      slot.hidden = n > state.boxSize;
    });
  }

  function refreshDropZoneLabels() {
    if (cfg.hasPhotoPerCupcake) {
      setText('dctb-drop-zone-title', 'Add all ' + state.boxSize + ' photos in one go');
      setText('dctb-drop-zone-btn-label', 'Choose ' + state.boxSize + ' photos →');
    }
  }

  function bindDropZone() {
    var btn = $('dctb-drop-zone-btn');
    if (!btn) return;
    on(btn, 'click', function(e) {
      e.preventDefault();
      var input = $('dctb-photo-input');
      if (!input) return;
      state.pendingTargetCupcake = 0;
      input.click();
    });
  }

  function bindBoxSlots() {
    if (!cfg.hasPhotoPerCupcake && !cfg.hasPhotoShared) return;
    $$('.dctb-box-slot').forEach(function(slot) {
      on(slot, 'click', function(e) {
        if (e.target.closest('.dctb-slot-controls')) return;
        var n = parseInt(slot.dataset.slot, 10);
        if (n > state.boxSize) return;
        if (cfg.hasPhotoShared) {
          if (state.sharedPhoto && state.sharedPhoto.dataUrl) {
            openCropperModal('shared');
          } else {
            var inp = $('dctb-photo-input');
            if (inp) inp.click();
          }
          return;
        }
        var photo = state.photos[n];
        if (photo && photo.dataUrl) {
          activateSlot(n);
        } else {
          state.pendingTargetCupcake = n;
          var input = $('dctb-photo-input');
          if (input) input.click();
        }
      });
      on(slot, 'keydown', function(e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          slot.click();
        }
      });
    });
  }

  function bindFileInput() {
    var input = $('dctb-photo-input');
    if (!input) return;
    on(input, 'change', function() {
      var files = Array.prototype.slice.call(input.files || []);
      var target = state.pendingTargetCupcake;
      input.value = '';
      state.pendingTargetCupcake = 0;

      if (!files.length) return;

      if (cfg.hasPhotoShared) {
        var file = files[0];
        if (file.type.indexOf('image/') !== 0) {
          alert('Please select an image file.');
          return;
        }
        if (file.size > 12 * 1024 * 1024) {
          alert('That image is over 12MB — please pick a smaller one.');
          return;
        }
        var reader = new FileReader();
        reader.onload = function(ev) {
          state.sharedPhoto = {
            file: file,
            dataUrl: ev.target.result,
            cropData: null,
            url: '', urlWidth: 0, urlHeight: 0
          };
          for (var i = 1; i <= state.boxSize; i++) {
            var slot = $('dctb-box-slot-' + i);
            var img = $('dctb-box-slot-photo-' + i);
            if (slot) slot.classList.add('is-filled');
            if (img) { img.src = ev.target.result; img.hidden = false; }
          }
          setText('dctb-drop-zone-btn-label', 'Change logo');
          updateProgress();
          updateStickyStatus();
          openCropperModal('shared');
        };
        reader.readAsDataURL(file);
        return;
      }

      if (target > 0) {
        var f = files[0];
        if (!validateImage(f)) return;
        readIntoSlot(f, target, true);
      } else {
        var nextSlot = findNextEmptySlot();
        for (var k = 0; k < files.length && nextSlot; k++) {
          var ff = files[k];
          if (!validateImage(ff)) continue;
          readIntoSlot(ff, nextSlot, false);
          nextSlot = findNextEmptySlot(nextSlot);
        }
      }
    });
  }

  function findNextEmptySlot(after) {
    var start = (after || 0) + 1;
    for (var i = start; i <= state.boxSize; i++) {
      if (!state.photos[i]) return i;
    }
    if (!after) {
      for (var j = 1; j <= state.boxSize; j++) {
        if (!state.photos[j]) return j;
      }
    }
    return null;
  }

  function validateImage(file) {
    if (file.type.indexOf('image/') !== 0) {
      alert('"' + file.name + '" doesn\'t look like an image. Skipped.');
      return false;
    }
    if (file.size > 12 * 1024 * 1024) {
      alert('"' + file.name + '" is over 12MB. Skipped.');
      return false;
    }
    return true;
  }

  function readIntoSlot(file, slotNum, openModal) {
    var reader = new FileReader();
    reader.onload = function(ev) {
      state.photos[slotNum] = {
        file: file,
        dataUrl: ev.target.result,
        cropData: null,
        url: '', urlWidth: 0, urlHeight: 0
      };
      loadPhotoIntoSlot(slotNum, openModal);
      updateProgress();
      updateStickyStatus();
      refreshAllDoneBanner();
      refreshDropZoneCompactness();
    };
    reader.readAsDataURL(file);
  }

  function loadPhotoIntoSlot(slotNum, openModal) {
    var photo = state.photos[slotNum];
    if (!photo || !photo.dataUrl) return;
    var slot = $('dctb-box-slot-' + slotNum);
    var slotImg = $('dctb-box-slot-photo-' + slotNum);
    if (!slotImg) return;
    if (slot) slot.classList.add('is-filled');
    slotImg.hidden = false;
    if (openModal) {
      slotImg.onload = function() { activateSlot(slotNum); };
    } else {
      slotImg.onload = null;
    }
    slotImg.src = photo.dataUrl;
  }

  function activateSlot(slotNum) {
    if (slotNum < 1 || slotNum > state.boxSize) return;
    destroyCropper();
    $$('.dctb-box-slot').forEach(function(s) { s.classList.remove('is-active'); });
    var slot = $('dctb-box-slot-' + slotNum);
    if (slot) slot.classList.add('is-active');
    state.activeCupcake = slotNum;
    var slotImg = $('dctb-box-slot-photo-' + slotNum);
    if (!slotImg) return;
    openCropperModal(slotNum);
  }

  function openCropperModal(slotOrShared) {
    var modal = $('dctb-img-editor-inline');
    var editorImg = $('dctb-photo-cropper-img');
    var badge = $('dctb-img-editor-badge');
    if (!modal || !editorImg) return;

    var isShared = slotOrShared === 'shared';
    var photo = isShared ? state.sharedPhoto : state.photos[slotOrShared];
    if (!photo) return;

    if (badge) badge.textContent = isShared ? 'Your logo' : ('Cupcake ' + slotOrShared);

    modal.hidden = false;
    modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('dctb-modal-open');

    editorImg.onload = function() { initCropper(slotOrShared); };
    editorImg.src = photo.dataUrl;
  }

  function closeCropperModal() {
    var modal = $('dctb-img-editor-inline');
    if (!modal) return;
    if (cropperInstance) {
      try {
        var croppedCanvas = cropperInstance.getCroppedCanvas({ maxWidth: 320, maxHeight: 320 });
        var dataUrl = croppedCanvas ? croppedCanvas.toDataURL('image/png') : null;

        if (cropperInstance.getData) {
          var d = cropperInstance.getData(true);
          var crop = {
            x: Math.round(d.x), y: Math.round(d.y),
            width: Math.round(d.width), height: Math.round(d.height)
          };
          if (state.sharedPhoto && modal.querySelector('#dctb-img-editor-badge') &&
              /logo/i.test($('dctb-img-editor-badge').textContent || '')) {
            state.sharedPhoto.cropData = crop;
          } else if (state.activeCupcake && state.photos[state.activeCupcake]) {
            state.photos[state.activeCupcake].cropData = crop;
          }
        }

        if (dataUrl) {
          var badge = $('dctb-img-editor-badge');
          var isShared = badge && /logo/i.test(badge.textContent || '');
          if (isShared) {
            for (var i = 1; i <= state.boxSize; i++) {
              var slotImg = $('dctb-box-slot-photo-' + i);
              if (slotImg) slotImg.src = dataUrl;
            }
          } else if (state.activeCupcake) {
            var singleImg = $('dctb-box-slot-photo-' + state.activeCupcake);
            if (singleImg) singleImg.src = dataUrl;
          }
        }
      } catch (e) {}
    }
    modal.hidden = true;
    modal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('dctb-modal-open');
    destroyCropper();
  }

  function initCropper(slotOrShared) {
    var editorImg = $('dctb-photo-cropper-img');
    if (!editorImg) return;
    var isShared = slotOrShared === 'shared';
    waitForCropper(function(available) {
      if (!available) {
        dwarn('[dctb] Cropper.js not available — skipping init');
        return;
      }
      destroyCropper();
      try {
        cropperInstance = new window.Cropper(editorImg, {
          aspectRatio: 1,
          viewMode: 3,
          dragMode: 'move',
          autoCropArea: 1,
          guides: false,
          center: true,
          highlight: false,
          cropBoxMovable: false,
          cropBoxResizable: false,
          toggleDragModeOnDblclick: false,
          background: false,
          modal: false,
          movable: true,
          zoomable: true,
          zoomOnTouch: true,
          zoomOnWheel: true,
          wheelZoomRatio: 0.1,
          minCropBoxWidth: 100,
          minCropBoxHeight: 100,
          crop: function(event) {
            var p = isShared ? state.sharedPhoto : state.photos[slotOrShared];
            if (!p) return;
            p.cropData = {
              x: Math.round(event.detail.x),
              y: Math.round(event.detail.y),
              width: Math.round(event.detail.width),
              height: Math.round(event.detail.height)
            };
          }
        });
      } catch (err) {
        dwarn('[dctb] Cropper init threw:', err);
      }
    });
  }

  function waitForCropper(cb) {
    if (typeof window.Cropper !== 'undefined') return cb(true);
    var tries = 0;
    var iv = setInterval(function() {
      if (typeof window.Cropper !== 'undefined') { clearInterval(iv); cb(true); }
      else if (++tries > 50) { clearInterval(iv); cb(false); }
    }, 100);
  }

  function destroyCropper() {
    if (cropperInstance) {
      try { cropperInstance.destroy(); } catch (e) {}
      cropperInstance = null;
    }
  }

  function bindCropperModalControls() {
    on($('dctb-img-editor-x'), 'click', function(e) { e.preventDefault(); closeCropperModal(); });
    on($('dctb-img-editor-done'), 'click', function(e) { e.preventDefault(); closeCropperModal(); });
    on($('dctb-photo-zoom-in'), 'click', function(e) { e.preventDefault(); if (cropperInstance) cropperInstance.zoom(0.1); });
    on($('dctb-photo-zoom-out'), 'click', function(e) { e.preventDefault(); if (cropperInstance) cropperInstance.zoom(-0.1); });
    on($('dctb-photo-reset'), 'click', function(e) { e.preventDefault(); if (cropperInstance) cropperInstance.reset(); });
    on($('dctb-photo-change'), 'click', function(e) {
      e.preventDefault();
      state.pendingTargetCupcake = state.activeCupcake;
      closeCropperModal();
      var input = $('dctb-photo-input');
      if (input) input.click();
    });

    var modal = $('dctb-img-editor-inline');
    on(modal, 'click', function(e) {
      if (e.target === modal) closeCropperModal();
    });
  }


  function refreshAllDoneBanner() {
    var banner = $('dctb-all-done');
    if (!banner) return;
    var allFilled = countFilledPhotos() >= state.boxSize;
    banner.hidden = !allFilled;
  }

  function refreshDropZoneCompactness() {
    var dz = $('dctb-drop-zone');
    if (!dz) return;
    var filled = countFilledPhotos();
    if (filled === 0) {
      dz.classList.remove('is-compact', 'is-hidden');
    } else if (filled >= state.boxSize) {
      dz.classList.remove('is-compact');
      dz.classList.add('is-hidden');
    } else {
      dz.classList.add('is-compact');
      dz.classList.remove('is-hidden');
      setText('dctb-drop-zone-title', (state.boxSize - filled) + ' more to go');
      setText('dctb-drop-zone-sub', 'Tap any empty cupcake, or upload more');
      setText('dctb-drop-zone-btn-label', 'Add more →');
    }
  }


  /* ══════════════════════════════════════════════════════════════
     PROGRESS + STICKY STATUS
     ══════════════════════════════════════════════════════════════ */
  function countFilledPhotos() {
    if (cfg.hasPhotoShared) return state.sharedPhoto ? state.boxSize : 0;
    var n = 0;
    for (var k in state.photos) {
      if (state.photos.hasOwnProperty(k) && parseInt(k, 10) <= state.boxSize && state.photos[k]) n++;
    }
    return n;
  }

  function updateProgress() {
    setText('dctb-preview-progress-count', countFilledPhotos() + ' of ' + state.boxSize + ' cupcakes');
    setText('dctb-preview-label-count', countFilledPhotos() + ' of ' + state.boxSize);
  }

  function updateStickyStatus() {
    var status = $('dctb-sticky-status');
    var btn = $('dctb-sticky-btn');
    if (!status) return;

    refreshStickyMeta();

    var missing = firstMissing();
    if (missing) {
      status.classList.remove('is-ready');
      status.innerHTML = '<strong>' + escapeHtml(missing) + '</strong>';
      if (btn) btn.disabled = true;
      return;
    }
    status.classList.add('is-ready');
    status.innerHTML = '<strong>Ready to order</strong>';
    if (btn) btn.disabled = false;
  }

  function calcAddonTotal() {
    var total = 0;

    if (state.gfOn) {
      var gfCfg = (cfg.gfByBoxSize || {})[state.boxSize === 12 ? 'box12' : 'box6'];
      if (gfCfg && typeof gfCfg.amount === 'number') total += gfCfg.amount;
    }

    if (state.frostingIsCustom) {
      total += 1000;
    }

    if (state.sprinkleIsPearl && state.sprinklePrice > 0) {
      total += state.sprinklePrice;
    }

    if (isCandleCardActive()) {
      var candleCfg = cfg.candles || {};
      if (isPillOn('rainbow') && candleCfg.rainbow && candleCfg.rainbow.price) {
        total += candleCfg.rainbow.price;
      }
      if (isPillOn('sparklers') && candleCfg.sparklers && candleCfg.sparklers.price) {
        total += candleCfg.sparklers.price;
      }
      if (isPillOn('metallic')) {
        var mTile = selectedTile('dctb-candle-sub-metallic');
        if (mTile) total += (parseInt(mTile.dataset.tilePrice, 10) || 0);
      }
      if (isPillOn('number')) {
        var nTile = selectedTile('dctb-candle-sub-number');
        var nDigits = numberCandleDigits();
        if (nTile && nDigits) {
          total += (parseInt(nTile.dataset.tilePrice, 10) || 0) * nDigits.length;
        }
      }
    }

    return total;
  }

  function refreshStickyMeta() {
    var meta = $('dctb-sticky-meta');
    if (!meta) return;
    var basePrice = 0;
    var activeBtn = document.querySelector('.dctb-size-card.is-active');
    if (activeBtn && activeBtn.dataset.variantPrice) {
      basePrice = parseInt(activeBtn.dataset.variantPrice, 10) || 0;
    } else {
      var v = cfg.variants || {};
      if (state.boxSize === 12 && v.box12) basePrice = v.box12.price || 0;
      else if (v.box6) basePrice = v.box6.price || 0;
    }
    var total = basePrice + calcAddonTotal();
    meta.textContent = state.boxSize + ' cupcakes · $' + (total / 100).toFixed(2);
  }

  /* Validation gate — walks every required state in display order and
     returns the FIRST missing piece. Returning null = ready to ATC.
     The dietary acks are checked alongside the toggles: turning on a
     toggle without confirming the disclaimer keeps the button disabled. */
  function firstMissing() {
    /* Size */
    if (cfg.boxSizes === 'both' && cfg.hasBox12) {
      var anyActive = document.querySelector('.dctb-size-card.is-active');
      if (!anyActive) return 'Pick a size';
    }
    /* Dietary acks — only required when their toggle is ON. */
    if (state.gfOn && !state.gfAck) return 'Confirm gluten note above';
    if (state.nutOn && !state.nutAck) return 'Confirm nut note above';
    /* Flavors */
    var need = flavorsRequired();
    var chosen = chosenFlavors().length;
    if (chosen < need) {
      var rem = need - chosen;
      return 'Pick ' + rem + ' more flavor' + (rem === 1 ? '' : 's');
    }
    /* Frosting (mode-dependent) */
    if (cfg.frostingMode === 'palette' || cfg.frostingMode === 'gender-reveal') {
      if (!state.frostingColor) return 'Pick a frosting color';
      if (cfg.twoToneRequired && !state.secondColor) return 'Pick a second color';
      if (cfg.hasTwoTone && state.twoToneOn && !state.secondColor) return 'Pick your second color';
    }
    /* Ribbon */
    if (cfg.hasRibbonPicker && !state.ribbonName) return 'Pick a ribbon color';
    /* Topper */
    if (cfg.hasTopperPerCupcake) {
      if (state.topperChunks.length === 0) return 'Type your cupcake message';
      /* Every cupcake must have a letter/icon. distributeTopper already
         caps at boxSize, so chunks > boxSize can't happen here — but
         chunks < boxSize is the common "partial message" case. Surface
         the exact remainder so customers know what's needed. */
      if (state.topperChunks.length < state.boxSize) {
        var togo = state.boxSize - state.topperChunks.length;
        return togo + ' more cupcake' + (togo === 1 ? '' : 's') + ' to fill';
      }
      if (cfg.topperColorSource === 'palette' && !state.topperColorName) return 'Pick a letter color';
    }
    /* Sprinkles */
    if (cfg.hasSprinkles && !state.sprinkleKey) return 'Pick sprinkles';
    /* Pearls (Butterfly) */
    if (cfg.hasPearls && !state.pearlKey) return 'Pick pearls';
    /* Filling (Gender Reveal) */
    if (cfg.hasFillingPicker && !state.fillingChoice) return 'Pick a filling';
    /* Photos */
    if (cfg.hasPhotoPerCupcake) {
      var filled = countFilledPhotos();
      if (filled < state.boxSize) {
        var togo = state.boxSize - filled;
        return togo + ' photo' + (togo === 1 ? '' : 's') + ' to go';
      }
    }
    if (cfg.hasPhotoShared && !state.sharedPhoto) {
      return 'Upload your logo';
    }
    /* Candles — optional, but if enabled require their sub-fields. */
    if (isCandleCardActive()) {
      if (isPillOn('metallic') && !selectedTile('dctb-candle-sub-metallic')) {
        return 'Pick a metallic candle color';
      }
      if (isPillOn('number')) {
        if (!selectedTile('dctb-candle-sub-number')) return 'Pick a number candle color';
        if (!numberCandleDigits())                   return 'Type your number';
      }
    }
    return null;
  }


  /* ══════════════════════════════════════════════════════════════
     CART ERROR BANNER
     ══════════════════════════════════════════════════════════════ */
  function showCartError(msg) {
    var banner = $('dctb-cart-error');
    setText('dctb-cart-error-msg', msg || 'Something went wrong adding to cart.');
    if (banner) {
      banner.classList.add('is-visible');
      banner.setAttribute('aria-hidden', 'false');
      banner.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }
  function hideCartError() {
    var banner = $('dctb-cart-error');
    if (banner) {
      banner.classList.remove('is-visible');
      banner.setAttribute('aria-hidden', 'true');
    }
  }
  function bindCartErrorClose() {
    on($('dctb-cart-error-close'), 'click', function(e) {
      e.preventDefault();
      hideCartError();
    });
  }


  /* ══════════════════════════════════════════════════════════════
     ADD TO CART
     ══════════════════════════════════════════════════════════════ */
  function bindStickyButton() {
    on($('dctb-sticky-btn'), 'click', function(e) {
      e.preventDefault();
      hideCartError();
      submitCart();
    });
  }

  function getActiveVariantId() {
    var activeBtn = document.querySelector('.dctb-size-card.is-active');
    if (activeBtn && activeBtn.dataset.variantId) return activeBtn.dataset.variantId;
    var v = cfg.variants || {};
    if (state.boxSize === 12 && v.box12) return String(v.box12.id);
    if (v.box6) return String(v.box6.id);
    return null;
  }

  function submitCart() {
    var missing = firstMissing();
    if (missing) {
      showCartError(missing + ' before adding to cart.');
      return;
    }

    var variantId = getActiveVariantId();
    if (!variantId) {
      showCartError('Could not determine variant — please refresh and try again.');
      return;
    }

    var btn = $('dctb-sticky-btn');
    if (btn) { btn.disabled = true; btn.textContent = 'Preparing…'; }

    var photoStage;
    if (cfg.hasPhotoPerCupcake) {
      if (btn) btn.textContent = 'Preparing photos…';
      photoStage = preparePerCupcakePhotos().then(function() {
        if (btn) btn.textContent = 'Uploading photos…';
        return uploadAllPhotos();
      });
    } else if (cfg.hasPhotoShared) {
      if (btn) btn.textContent = 'Uploading logo…';
      photoStage = uploadSharedPhoto();
    } else {
      photoStage = Promise.resolve();
    }

    photoStage
      .then(function() {
        if (btn) btn.textContent = 'Adding to cart…';
        return addBaseCupcake(variantId);
      })
      .then(function(cakeKey) {
        return addAddons(cakeKey);
      })
      .then(function() {
        if (btn) btn.textContent = 'Added ✓';
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        setTimeout(function() { window.location.href = '/cart'; }, 500);
      })
      .catch(function(err) {
        console.error('[dctb] cart error:', err);
        showCartError(err && err.message ? err.message : "We couldn't add to cart. Please try again.");
        if (btn) { btn.disabled = false; btn.textContent = 'Add to cart'; }
      });
  }

  function preparePerCupcakePhotos() {
    if (state.activeCupcake && cropperInstance) {
      try {
        var p = state.photos[state.activeCupcake];
        if (p && cropperInstance.getData) {
          var d = cropperInstance.getData(true);
          p.cropData = {
            x: Math.round(d.x), y: Math.round(d.y),
            width: Math.round(d.width), height: Math.round(d.height)
          };
        }
      } catch (e) {}
    }
    closeCropperModal();
    return Promise.resolve();
  }

  function uploadAllPhotos() {
    var pending = [];
    for (var n = 1; n <= state.boxSize; n++) {
      var p = state.photos[n];
      if (!p || !p.dataUrl) continue;
      pending.push(renderAndUpload(n, p));
    }
    return Promise.all(pending);
  }

  function renderAndUpload(slotNum, photo) {
    return new Promise(function(resolve) {
      var img = new Image();
      img.onload = function() {
        var canvas = renderCroppedCircular(img, photo.cropData, 1200);
        uploadCanvasWithTimeout(canvas).then(function(data) {
          if (data && data.secure_url) {
            photo.url = data.secure_url;
            photo.urlWidth = data.width;
            photo.urlHeight = data.height;
          }
          resolve();
        });
      };
      img.onerror = function() { resolve(); };
      img.src = photo.dataUrl;
    });
  }

  function uploadSharedPhoto() {
    if (!state.sharedPhoto || !state.sharedPhoto.dataUrl) return Promise.resolve();
    return new Promise(function(resolve) {
      var img = new Image();
      img.onload = function() {
        var canvas = renderCroppedCircular(img, state.sharedPhoto.cropData, 1200);
        uploadCanvasWithTimeout(canvas).then(function(data) {
          if (data && data.secure_url) {
            state.sharedPhoto.url = data.secure_url;
            state.sharedPhoto.urlWidth = data.width;
            state.sharedPhoto.urlHeight = data.height;
          }
          resolve();
        });
      };
      img.onerror = function() { resolve(); };
      img.src = state.sharedPhoto.dataUrl;
    });
  }

  function renderCroppedCircular(img, cropData, size) {
    var sx, sy, sw, sh;
    if (cropData) {
      sx = cropData.x; sy = cropData.y; sw = cropData.width; sh = cropData.height;
    } else {
      var side = Math.min(img.naturalWidth, img.naturalHeight);
      sx = (img.naturalWidth - side) / 2;
      sy = (img.naturalHeight - side) / 2;
      sw = side; sh = side;
    }
    var out = Math.min(size, Math.max(sw, 100));
    var square = document.createElement('canvas');
    square.width = out; square.height = out;
    var sctx = square.getContext('2d');
    sctx.imageSmoothingEnabled = true;
    sctx.imageSmoothingQuality = 'high';
    sctx.drawImage(img, sx, sy, sw, sh, 0, 0, out, out);
    var round = document.createElement('canvas');
    round.width = out; round.height = out;
    var rctx = round.getContext('2d');
    rctx.beginPath();
    rctx.arc(out / 2, out / 2, out / 2, 0, Math.PI * 2, true);
    rctx.closePath();
    rctx.clip();
    rctx.drawImage(square, 0, 0);
    return round;
  }

  function uploadCanvasWithTimeout(canvas) {
    /* Convert the canvas to a blob once, then try the Cloudinary upload
       up to 3 times (each attempt races an 8s timeout). A single slow
       response or a dropped connection used to silently lose the photo
       and the order fell back to "Uploaded (awaiting email)"; retrying
       means transient failures recover on their own. */
    function toBlobPromise() {
      return new Promise(function(resolve, reject) {
        try {
          canvas.toBlob(function(blob) {
            if (!blob) return reject(new Error('blob null'));
            resolve(blob);
          }, 'image/png');
        } catch (e) { reject(e); }
      });
    }
    function attemptUpload(blob) {
      var upload = new Promise(function(resolve, reject) {
        var form = new FormData();
        form.append('file', blob, 'cupcake-photo.png');
        form.append('upload_preset', CLOUDINARY_PRESET);
        fetch(CLOUDINARY_URL, { method: 'POST', body: form })
          .then(function(r) {
            if (!r.ok) throw new Error('Cloudinary HTTP ' + r.status);
            return r.json();
          })
          .then(function(data) {
            if (!data || !data.secure_url) throw new Error('No URL returned');
            resolve(data);
          })
          .catch(reject);
      });
      var timeout = new Promise(function(resolve) {
        setTimeout(function() { resolve(null); }, UPLOAD_TIMEOUT_MS);
      });
      return Promise.race([upload, timeout]).catch(function() { return null; });
    }
    return toBlobPromise().then(function(blob) {
      return attemptUpload(blob).then(function(d1) {
        if (d1) return d1;
        return attemptUpload(blob).then(function(d2) {
          if (d2) return d2;
          return attemptUpload(blob);
        });
      });
    }).catch(function() { return null; });
  }

  function buildBaseProperties() {
    var props = {};

    /* Flavors — each slot is 3 cupcakes, so we list "Vanilla (3)" etc.
       For nut acknowledgment, we append (nut acknowledged) inline so
       the cart card shows it next to the relevant flavor. */
    var need = flavorsRequired();
    var perFlavor = 3;
    var flavorParts = [];
    for (var i = 0; i < need; i++) {
      if (state.flavors[i]) flavorParts.push(state.flavors[i] + ' (' + perFlavor + ')');
    }
    if (flavorParts.length) props['Flavors'] = flavorParts.join(' · ');

    /* Combined Dietary line — surface gluten-free + nut-free together
       with explicit acknowledgment marker. Matches the cart copy from
       the regular and mini cupcake builders. */
    var diet = [];
    if (state.gfOn) diet.push('Gluten-free');
    if (state.nutOn) diet.push('Nut Allergy');
    if (diet.length) {
      props['Dietary'] = diet.join(' + ') + ' (acknowledged)';
    }

    /* Frosting */
    if (cfg.frostingMode === 'palette' || cfg.frostingMode === 'gender-reveal') {
      var fc = state.frostingColor;
      if (state.frostingIsCustom) fc = 'Custom ' + state.frostingHex + ' (+$10)';
      if (state.twoToneOn && state.secondColor) fc += ' + ' + state.secondColor + ' (two-tone)';
      if (fc) props['Frosting'] = fc;
    } else if (cfg.frostingMode === 'locked-rainbow') {
      props['Frosting'] = 'Signature rainbow swirl';
    } else if (cfg.frostingMode === 'locked-cream') {
      props['Frosting'] = 'Cream buttercream';
    }

    /* Ribbon */
    if (cfg.hasRibbonPicker && state.ribbonName) {
      props['Ribbon color'] = state.ribbonName;
    }

    /* Topper */
    if (cfg.hasTopperPerCupcake && state.topperChunks.length) {
      props['Cupcake message'] = state.topperRaw;
      props['Per cupcake'] = state.topperChunks.map(function(c, i) {
        return '#' + (i + 1) + ': ' + c;
      }).join(' · ');
      if (state.topperColorName) {
        props['Letter color'] = state.topperColorName + (cfg.topperColorSource === 'ribbon' ? ' (matches ribbon)' : '');
      }
    }

    /* Sprinkles */
    if (cfg.hasSprinkles && state.sprinkleName) {
      var sLabel = state.sprinkleName;
      if (state.sprinklePrice > 0) sLabel += ' (+$' + (state.sprinklePrice / 100).toFixed(2) + ')';
      props['Sprinkles'] = sLabel;
    }

    /* Pearls */
    if (cfg.hasPearls && state.pearlName) {
      props['Pearls'] = state.pearlName;
    }

    /* Filling */
    if (cfg.hasFillingPicker && state.fillingChoice) {
      props['Filling'] = state.fillingChoice === 'pink' ? 'Pink (it\'s a girl)' : 'Blue (it\'s a boy)';
    }

    /* Per-cupcake photos */
    if (cfg.hasPhotoPerCupcake) {
      for (var n = 1; n <= state.boxSize; n++) {
        var p = state.photos[n];
        if (!p) continue;
        var key = 'Photo #' + n;
        if (p.url) {
          props[key] = p.url;
          if (p.urlWidth && p.urlHeight) props['Photo size #' + n] = p.urlWidth + 'x' + p.urlHeight + ' px';
          if (p.cropData) {
            var c = p.cropData;
            props['Photo crop #' + n] = 'x:' + c.x + ', y:' + c.y + ', ' + c.width + 'x' + c.height + ' px';
          }
        } else {
          props[key] = 'Uploaded (awaiting email): ' + (p.file && p.file.name ? p.file.name : 'image');
        }
      }
    }

    /* Shared logo */
    if (cfg.hasPhotoShared && state.sharedPhoto) {
      if (state.sharedPhoto.url) {
        props['Logo'] = state.sharedPhoto.url;
        if (state.sharedPhoto.urlWidth && state.sharedPhoto.urlHeight) {
          props['Logo size'] = state.sharedPhoto.urlWidth + 'x' + state.sharedPhoto.urlHeight + ' px';
        }
      } else {
        props['Logo'] = 'Uploaded (awaiting email): ' + (state.sharedPhoto.file && state.sharedPhoto.file.name ? state.sharedPhoto.file.name : 'logo');
      }
    }

    /* Gift message */
    if (giftCardActive()) {
      var giftText = giftMessageText();
      if (giftText) {
        props['Gift message'] = giftText;
      }
    }

    /* Candles summary */
    var candleSummary = candleSummaryPieces();
    if (candleSummary.length) {
      props['Candles'] = candleSummary.join(' + ');
    }

    return props;
  }

  function addBaseCupcake(variantId) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({
        items: [{ id: parseInt(variantId, 10), quantity: 1, properties: buildBaseProperties() }]
      })
    })
    .then(function(r) { return r.json(); })
    .then(function(data) {
      if (data.status) throw new Error(data.description || 'Cart add failed');
      var key = (data.items && data.items[0] && data.items[0].key) || null;
      dlog('[dctb] base cake key:', key);
      return key;
    });
  }

  function addAddons(cakeKey) {
    var addons = [];
    var tag = cakeKey ? { '_addon_for': cakeKey } : {};

    /* GF fee — only the GF toggle creates a paid line item. Nut-Free
       is free, so there's no nut-free addon variant to push. */
    if (state.gfOn) {
      var gfCfg = (cfg.gfByBoxSize || {})[state.boxSize === 12 ? 'box12' : 'box6'];
      if (gfCfg && gfCfg.variantId) {
        addons.push({
          id: parseInt(gfCfg.variantId, 10),
          quantity: 1,
          properties: Object.assign({ '_addon': 'Gluten-free fee' }, tag)
        });
      } else {
        dwarn('[dctb] No GF variant configured for box size', state.boxSize);
      }
    }

    /* Custom color +$10 */
    if (state.frostingIsCustom) {
      var ccId = lookupOptionVariant('Custom color');
      if (ccId) {
        addons.push({
          id: parseInt(ccId, 10),
          quantity: 1,
          properties: Object.assign({ '_addon': 'Custom frosting color' }, tag)
        });
      } else {
        dwarn('[dctb] Custom color variant not found in ddbOptionVariants');
      }
    }

    /* Pearl sprinkles +$10 */
    if (state.sprinkleIsPearl && state.sprinklePrice > 0) {
      var sId = lookupOptionVariant('Pearl sprinkles')
             || lookupOptionVariant('Sprinkles — ' + state.sprinkleName)
             || lookupOptionVariant('Sprinkles - ' + state.sprinkleName)
             || lookupOptionVariant('Sprinkle upgrade');
      if (sId) {
        addons.push({
          id: parseInt(sId, 10),
          quantity: 1,
          properties: Object.assign({ '_addon': 'Pearl sprinkles' }, tag)
        });
      } else {
        dwarn('[dctb] No pearl sprinkle variant found');
      }
    }

    /* Candles */
    if (isCandleCardActive()) {
      var candleCfg = cfg.candles || {};
      var rainbowOn   = isPillOn('rainbow');
      var sparklersOn = isPillOn('sparklers');
      var metallicOn  = isPillOn('metallic');
      var numberOn    = isPillOn('number');

      if (rainbowOn && candleCfg.rainbow && candleCfg.rainbow.id) {
        addons.push({
          id: parseInt(candleCfg.rainbow.id, 10),
          quantity: 1,
          properties: Object.assign({ '_addon': 'Rainbow candles' }, tag)
        });
      }
      if (sparklersOn && candleCfg.sparklers && candleCfg.sparklers.id) {
        addons.push({
          id: parseInt(candleCfg.sparklers.id, 10),
          quantity: 1,
          properties: Object.assign({ '_addon': 'Sparklers' }, tag)
        });
      }
      if (metallicOn) {
        var mTile = selectedTile('dctb-candle-sub-metallic');
        if (mTile && mTile.dataset.tileVariantId) {
          var mColor = mTile.dataset.tileValue || '';
          addons.push({
            id: parseInt(mTile.dataset.tileVariantId, 10),
            quantity: 1,
            properties: Object.assign({
              '_addon': 'Metallic candles' + (mColor ? ' (' + mColor + ')' : '')
            }, tag)
          });
        }
      }
      if (numberOn) {
        var nTile = selectedTile('dctb-candle-sub-number');
        var nDigits = numberCandleDigits();
        if (nTile && nTile.dataset.tileVariantId && nDigits) {
          var nColor = nTile.dataset.tileValue || '';
          addons.push({
            id: parseInt(nTile.dataset.tileVariantId, 10),
            quantity: nDigits.length,
            properties: Object.assign({
              'Number': nDigits,
              '_addon': 'Number candles' + (nColor ? ' (' + nColor + ')' : '')
            }, tag)
          });
        }
      }
    }

    if (!addons.length) return Promise.resolve();

    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: addons })
    })
    .then(function(r) { return r.json(); })
    .then(function(data) {
      if (data.status) {
        dwarn('[dctb] addon add failed (continuing):', data);
      } else {
        dlog('[dctb] addons added:', addons.length);
      }
    });
  }


})();